import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  HStack,
  Heading,
  InputGroup,
  InputLeftAddon,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useAuth } from "hooks/useAuth";
import { authService } from "services/auth.service";
import Card from "components/card/Card.js";
import { useToastNotification } from "utils/toast-utils";
import UpdatePasswordModal from "components/modals/UpdatePasswordModal";
import { useFormik } from "formik";
import { profileUpdateSchema } from "views/user/schemas";

const UserProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const { showToast } = useToastNotification();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      companyName: user?.companyName || "",
      email: user?.email || "",
      rif: user?.rif || "",
      companyPhoneNumber: user?.companyPhoneNumber || "",
      contactName: user?.contactPersons?.[0]?.name || "",
      contactPhoneNumber: user?.contactPersons?.[0]?.phoneNumber || "",
      bankAccountName: user?.bankInformation?.accountName || "",
      bankAccountNumber: user?.bankInformation?.accountNumber || "",
    },
    validationSchema: profileUpdateSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        contactName: values.contactName,
        companyName: values.companyName,
        companyPhoneNumber: values.companyPhoneNumber,
        contactPhoneNumber: values.contactPhoneNumber,
        bankAccountName: values.bankAccountName,
        bankAccountNumber: values.bankAccountNumber,
        rif: values.rif,
      };

      try {
        const updatedUser = await authService.updateProfile(payload);
        setIsEditing(false);
        showToast("Success", "Profile updated successfully", "success");
      } catch (error) {
        console.error("Error updating profile:", error);
        showToast(
          "Error",
          error.response?.data?.message || "Failed to update profile",
          "error"
        );
      }
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEditing(false);
  };

  const handleChangePassword = () => {
    setIsPasswordModalOpen(true);
  };

  return (
    <>
      <Card p="0">
        <Flex direction="column" gap={4}>
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="lg">Profile</Heading>
              <Text color="gray.500">{formik.values.email}</Text>
            </Box>
            {!isEditing ? (
              <Button colorScheme="purple" onClick={handleEdit}>
                Edit
              </Button>
            ) : (
              <HStack spacing={2}>
                <Button
                  colorScheme="purple"
                  onClick={formik.handleSubmit}
                  isDisabled={!formik.isValid || formik.isSubmitting}
                >
                  Save
                </Button>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </HStack>
            )}
          </Flex>

          {/* Company Info */}
          <Card p="0">
            <Heading size="md" mb={4}>
              Company Info
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack
                spacing={4}
                align="stretch"
                display={{ base: "block", md: "flex" }}
              >
                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.companyName && formik.errors.companyName
                  }
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Company Name
                  </FormLabel>
                  <Input
                    name="companyName"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Company name"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={true}
                  />
                  <FormErrorMessage>
                    {formik.errors.companyName}
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={formik.touched.email && formik.errors.email}
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Email
                  </FormLabel>
                  <Input
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="company@email.com"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={true}
                  />
                  <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack
                spacing={4}
                align="stretch"
                display={{ base: "block", md: "flex" }}
              >
                <FormControl
                  isRequired
                  isInvalid={formik.touched.rif && formik.errors.rif}
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    RIF
                  </FormLabel>
                  <Input
                    name="rif"
                    value={formik.values.rif}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="J-12345678-9"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={true}
                  />
                  <FormErrorMessage>{formik.errors.rif}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.companyPhoneNumber &&
                    formik.errors.companyPhoneNumber
                  }
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Phone Number
                  </FormLabel>
                  <Input
                    name="companyPhoneNumber"
                    value={formik.values.companyPhoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="+12123456789"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={!isEditing}
                  />
                  <FormErrorMessage>
                    {formik.errors.companyPhoneNumber}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
          </Card>

          {/* Contact Person */}
          <Card p="0">
            <Heading size="md" mb={4}>
              Contact Person
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack
                spacing={4}
                align="stretch"
                display={{ base: "block", md: "flex" }}
              >
                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.contactName && formik.errors.contactName
                  }
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Name
                  </FormLabel>
                  <Input
                    name="contactName"
                    value={formik.values.contactName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Contact name"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={!isEditing}
                  />
                  <FormErrorMessage>
                    {formik.errors.contactName}
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.contactPhoneNumber &&
                    formik.errors.contactPhoneNumber
                  }
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Phone Number
                  </FormLabel>
                  <Input
                    name="contactPhoneNumber"
                    value={formik.values.contactPhoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="+12123456789"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={!isEditing}
                  />
                  <FormErrorMessage>
                    {formik.errors.contactPhoneNumber}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
          </Card>

          {/* Bank Information */}
          <Card p="0">
            <Heading size="md" mb={4}>
              Bank Information
            </Heading>
            <VStack spacing={4} align="stretch">
              <HStack
                spacing={4}
                align="stretch"
                display={{ base: "block", md: "flex" }}
              >
                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.bankAccountName &&
                    formik.errors.bankAccountName
                  }
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Account Holder Name
                  </FormLabel>
                  <Input
                    name="bankAccountName"
                    value={formik.values.bankAccountName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Account holder name"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={!isEditing}
                  />
                  <FormErrorMessage>
                    {formik.errors.bankAccountName}
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.bankAccountNumber &&
                    formik.errors.bankAccountNumber
                  }
                >
                  <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                    Account Number
                  </FormLabel>
                  <Input
                    name="bankAccountNumber"
                    value={formik.values.bankAccountNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="0000000000"
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="6px"
                    isReadOnly={!isEditing}
                  />
                  <FormErrorMessage>
                    {formik.errors.bankAccountNumber}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
          </Card>

          {/* Change Password */}
          {isEditing && (
            <Card>
              <Heading size="md" mb={4}>
                Change Password
              </Heading>
              <Button
                variant="outline"
                colorScheme="purple"
                border="1px solid"
                borderRadius="4px"
                onClick={handleChangePassword}
                alignSelf={{ base: "center", md: "flex-start" }}
                width={{ base: "100%", md: "235px" }}
              >
                Change Password
              </Button>
            </Card>
          )}
        </Flex>
      </Card>
      <UpdatePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </>
  );
};

export default UserProfile;
